import { isScreenshotChildType } from "./documentChildTypes";

type SessionDocumentLike = {
  documentId: string;
  fetchableDocument?: {
    childType?: string | null;
    parentDocumentId?: string | null;
  } | null;
};

type CitationLike = { documentId: string };

/**
 * Ids of the source documents whose screenshot is cited among `citations`.
 *
 * A citation of a screenshot child document (see `SCREENSHOT_CHILD_TYPES`)
 * contributes its parent document's id. Screenshot documents that exist on the
 * session but are not cited (e.g. left over from a superseded run) do not
 * count, so a source document only has a screenshot when one is displayed
 * alongside its citations.
 */
export function getDocumentIdsWithCitedScreenshots(
  citations: CitationLike[],
  documents: SessionDocumentLike[],
): Set<string> {
  const citedDocumentIds = new Set(citations.map((c) => c.documentId));
  const screenshotSourceIds = new Set<string>();

  for (const doc of documents) {
    const fetchable = doc.fetchableDocument;
    if (
      citedDocumentIds.has(doc.documentId) &&
      fetchable?.parentDocumentId != null &&
      isScreenshotChildType(fetchable.childType)
    ) {
      screenshotSourceIds.add(fetchable.parentDocumentId);
    }
  }

  return screenshotSourceIds;
}

/**
 * Drops citations of source documents whose screenshot is cited, so the
 * screenshot is shown in their place. A source citation is always kept when no
 * screenshot of its document is cited.
 *
 * @param citations Citations to filter.
 * @param documents Session documents, used to resolve a screenshot's parent.
 * @param hideSourceDocs Whether "Show screenshots only" is on.
 * @param displayedCitations Every citation being displayed, used to find the
 *   cited screenshots when `citations` is only a slice of them (e.g. one
 *   document's or one step's citations). Defaults to `citations`.
 */
export function filterSourceDocCitationsFromSession<T extends CitationLike>(
  citations: T[],
  documents: SessionDocumentLike[],
  hideSourceDocs: boolean,
  displayedCitations: CitationLike[] = citations,
): T[] {
  if (!hideSourceDocs) return citations;

  const screenshotSourceIds = getDocumentIdsWithCitedScreenshots(
    displayedCitations,
    documents,
  );

  return citations.filter((c) => !screenshotSourceIds.has(c.documentId));
}

/**
 * Whether "Show screenshots only" would change anything: some citation is of a
 * source document whose screenshot is also cited.
 */
export function hasScreenshotCitations(
  citations: CitationLike[],
  documents: SessionDocumentLike[],
): boolean {
  const screenshotSourceIds = getDocumentIdsWithCitedScreenshots(
    citations,
    documents,
  );
  return citations.some((c) => screenshotSourceIds.has(c.documentId));
}
